import { isArray, isNonEmptyString, isString } from '@mbrtargeting/metatag-utils';
import { inject, injectionTarget } from '../../../loader/decorators/inject.js';
import { onPhase, Phase } from '../../../loader/decorators/on-phase.js';
import { ConfigResolver } from '../../../loader/essentials/config-resolver.js';
import { selectModuleSettings } from '../../../loader/essentials/config-selector.js';
import { logger } from '../../../loader/essentials/logger.js';
import { ConsentCache } from '../../../loader/modules/consent-cache.js';
import { CONFIG_RESOLVER, CONSENT_CACHE } from '../../../loader/token.js';
import { sendCorsRequestPromise } from '../../../utils/send-cors-request.js';
import { GLOBAL_TARGETING, ID_STORE } from '../../token.js';
import { GlobalTargeting } from '../global-targeting/global-targeting.js';
import { IdStore } from '../idstore/idstore.js';

const log = logger.logGroup({ prefix: 'MbrId' });

interface MbrIdResponse {
    userId?: string; //adscale user id
    mbrUserId?: string, //no longer used
    mbrSegments?: string[], //segments based on Ströer DMP
    mbrCategories?: string[], //IAB Content Taxonomy segments
    mbrSegmentsTest?: string[], //test, can be ignored
    adexSegments?: string[], //segments based on TheAdex DMP (different from mbrSegments)
}

@injectionTarget()
export class MbrId {

    @inject(CONFIG_RESOLVER) #configResolver!: ConfigResolver;
    @inject(CONSENT_CACHE) #consentCache!: ConsentCache;
    @inject(ID_STORE) #idStore!: IdStore;
    @inject(GLOBAL_TARGETING) #globalTargeting!: GlobalTargeting;

    /**
     * requests the mbrId and writes it to storage. If the request fails it writes the id as "failed" and sends it anyway
     *
     * @example response
     * {
     *     "userId": "ff0da6f1912c385d8f1d82e9bc9c2114",
     *     "mbrSegments": ["855125d3", "b3838f1f", "7a97658b", "8a1a9d78", "980f3875", "825a837e"],
     *     "mbrCategories": ["c1347","7afb3","29e18","88f1a","2500f"],
     *     "adexSegments": ["225031", "122226", "224878", "132964", "123303", "169597", "116175","227944"],
     * }
     */
    @onPhase({ phase: Phase.TARGETING })
    async requestUserId() {
        const tcData = await this.#consentCache.getTcDataPromise();
        const gdprApplies: boolean = tcData?.gdprApplies || false;
        const consentString = tcData?.tcString || '';
        const { iabVendorId = 0, active = false, url = '' } = this.#configResolver.get(selectModuleSettings('MBR_USER_ID')) || {};
        const isAllowed = this.#consentCache.ifConsentConditions([
            { consentedVendors: [iabVendorId] },
            { legitimateInterestVendors: [iabVendorId] },
            { gdprApplies: false },
        ]);

        if (!active || url.length === 0 || !isAllowed) return;

        const referrer = location.protocol + '//' + location.hostname + location.pathname; //without queries
        const paramDelimiter = url.indexOf('?') === -1 ? '?' : '&';
        const src = `${url}${paramDelimiter}gdpr=${gdprApplies.toString()}&gdpr_consent=${consentString}&ref=${encodeURI(referrer)}`;

        const safeValues = (x: unknown) => isArray(x) && x.every(isString) ? x : [];
        await sendCorsRequestPromise<MbrIdResponse>({
            src,
            withCredentials: true,
            // onsuccsess
        }).then(({ userId, mbrSegments, mbrCategories, mbrSegmentsTest, adexSegments }) => {

            //adexSegments can include several hundred values, use max 100 values https://stroeerdigitalgroup.atlassian.net/browse/METATAG-845
            const maxedAdexSegments = safeValues(adexSegments).slice(0, 100)

            //segments are only used in Google Ad Manager to book campaigns upon.
            //Other ad servers get these values via different means (ex: Ströer-Core direct connection between DMP & SSP)
            this.#globalTargeting.setKeyValues({
                mbrseg: safeValues(mbrSegments),
                mbrct: safeValues(mbrCategories),
                mbrsegtest: safeValues(mbrSegmentsTest),
                adexseg: maxedAdexSegments,
            });

            //id will likely always be filled. Don't propagate empty/undefined ids
            if(!isNonEmptyString(userId)) return;

            //id will be used to identify user to other services, ex: audienceproject
            this.#idStore.setIdPromise('adscaleid', userId).then(() => {
                log.debug('saved id "%s"', [userId]);
            });

        }).catch((error) => {
            log.debug('CORS request for mbrUserId failed, error:', [error]);
            const id = 'failed';

            //failed id might be because of bad connection, will be re-assed/overwritten on next page visit
            this.#idStore.setIdPromise('adscaleid', id).then(() => {
                log.debug('saved id "%s"', [id]);
            });
        });
    }
}
